<template>
  <tr
    @click="handlePlayerClick"
    class="whitespace-nowrap border-t border-t-gray-950 flex flex-col lg:table-row"
    :class="[
      isCurrentUser ? 'box-border shadow-[inset_2px_0_0_0_#4E54C8] selected-user' : '',
      player?.uuid ? 'cursor-pointer' : '',
    ]"
  >
    <!-- Player Info Cell -->
    <td class="py-3 px-4 flex items-center space-x-3">
      <component
        :is="props.player?.uuid ? 'router-link' : 'div'"
        v-bind="props.player?.uuid ? { to: { name: 'UserProfile', params: { uuid: props.player.uuid } } } : {}"
        class="flex items-center space-x-3"
        @click.stop
      >
        <img :src="playerAvatar" :alt="$t('coach.alt.avatar')" class="w-12 h-12 rounded-full" />
        <div class="flex flex-col items-start gap-1">
          <span class="flex gap-2 items-center flex-wrap">
            {{ playerName }}
            <img v-if="player.is_subscriber" src="../../../../assets/logos/refrag-logo-holo.png" :alt="$t('coach.alt.avatar')" class="w-4 h-4" />
            <img v-else-if="player.uuid" src="../../../../assets/logos/refrag-icon-button.svg" :alt="$t('coach.alt.avatar')" class="w-4 h-4" />
            <span
              v-for="(role, i) in displayRoles"
              :key="i"
              class="hidden lg:inline-flex items-center justify-center px-1 py-0.5 gap-0.5 rounded"
              :style="{ background: role.color }"
            >
              <RoleIcon :role="role.key" :color="role.text" />
              <span class="text-[12px] leading-[12px] font-medium" :style="{ color: role.text }">{{ $t(`coach.roles.${role.key}`) }}</span>
            </span>
          </span>
          <div class="flex items-center gap-1">
            <Rank
              v-if="platform !== 'refrag'"
              :platform="platform"
              :rank="player.end_rank"
              :matchType="matchType"
              :cs_map="csMap"
            />
            <template v-if="player.start_rank && player.end_rank">
              <span v-if="player.end_rank - player.start_rank > 1" class="text-xs text-success-400">+{{ player.end_rank - player.start_rank }}</span>
              <ArrowUpIcon v-else-if="player.end_rank > player.start_rank" class="w-4 h-4 text-success-400" />
              <span v-if="player.start_rank - player.end_rank > 1" class="text-xs text-danger-400">-{{ player.start_rank - player.end_rank }}</span>
              <ArrowDownIcon v-else-if="player.start_rank > player.end_rank" class="w-4 h-4 text-danger-400" />
            </template>
          </div>
        </div>
      </component>
    </td>

    <!-- Desktop: Individual stat cells -->
    <template v-for="column in columns" :key="column.key">
      <td class="py-2 px-4 hidden lg:table-cell">
        <StatTableCell
          :player="player"
          :column="column"
        />
      </td>
    </template>

    <!-- Mobile: Reuse StatTableCell with different layout -->
    <td class="lg:hidden p-0">
      <div class="bg-gray-900 px-3 py-4 w-full">
        <div class="grid grid-cols-3 w-full gap-4">
          <template v-for="column in columns" :key="column.key">
            <div class="flex flex-col col-span-1 gap-2">
              <h2 class="text-text-secondary text-sm text-center">{{ column.label }}</h2>
              <h2 class="text-sm text-center">
                <StatTableCell
                  :player="player"
                  :column="column"
                />
              </h2>
            </div>
          </template>
        </div>
      </div>
    </td>
  </tr>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/vue/24/outline';
import Rank from '../display/Rank.vue';
import RoleIcon from '../display/RoleIcon.vue';
import StatTableCell from './StatTableCell.vue';

import unknownUser from '../../../../assets/unknown-user.png';

const props = defineProps({
  player: {
    type: Object,
    required: true,
  },
  columns: {
    type: Array,
    required: true,
  },
  platform: {
    type: String,
    required: true,
  },
  matchType: {
    type: String,
    required: true,
  },
  csMap: {
    type: String,
    required: true,
  },
  currentUserId: {
    type: String,
    default: null,
  },

});

const router = useRouter();
const i18n = useI18n();

const playerName = computed(() => props.player?.display_name || i18n.t('coach.player_row.unknown_player'));

const playerAvatar = computed(() => props.player?.avatar || unknownUser);

const isCurrentUser = computed(() => props.player?.steam_id === props.currentUserId);

// Labels live in coach.roles.<key>
const ROLES = {
  rifle: { key: 'rifle', color: 'rgba(249,95,95,0.15)', text: '#F95F5F' },
  awp: { key: 'awp', color: 'rgba(249,95,144,0.15)', text: '#F95F90' },
  entry: { key: 'entry', color: 'rgba(95,151,249,0.15)', text: '#5F97F9' },
  lurker: { key: 'lurker', color: 'rgba(167,139,250,0.15)', text: '#A78BFA' },
  support: { key: 'support', color: 'rgba(95,249,218,0.15)', text: '#5FF9DA' },
  pistol: { key: 'pistol', color: 'rgba(249,193,95,0.15)', text: '#F9C15F' },
};

const displayRoles = computed(() => {
  const ct = props.player?.ct_role;
  const t = props.player?.t_role;
  const ctData = ROLES[ct] || null;
  const tData = ROLES[t] || null;

  // Same role on both sides — show once
  if (ct === t) return ctData ? [ctData] : [];

  // Two different roles — if one is rifle (generic), only show the other
  if (ct === 'rifle' && tData) return [tData];
  if (t === 'rifle' && ctData) return [ctData];

  // Two distinct non-rifle roles — show both
  return [ctData, tData].filter(Boolean);
});

const handlePlayerClick = () => {
  if (props.player?.uuid) {
    router.push(`/user/${props.player.uuid}`);
  }
};

</script>
